import { RecordKeys } from '~/common/types';
import { isNil } from 'lodash-es';
import { useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApplicationContext } from '~/common/context';
import { FEATURES, isGranted } from '~/common/helpers';
import {
  AccessDecisionStrategy,
  FeatureFlagEnum,
  IFeatureFlags,
  IUserAuth,
} from '~/common/types';

const featureFlagsLookup: Record<FeatureFlagEnum, RecordKeys<IFeatureFlags>> = {
  [FEATURES.QUOTE]: 'enabledQuote',
  [FEATURES.REDISTRIBUTION]: 'enabledRedistribution',
  [FEATURES.QUOTE_EXPRESS]: 'enabledQuoteExpress',
  [FEATURES.CHAT]: 'enabledChat',
  [FEATURES.CONTAINER]: 'enabledContainer',
  // Add other features as needed
};
export const useAccessControl = () => {
  const { auth, application } = useApplicationContext();

  const isGrantedFn = useCallback(
    (requiredRoles: Array<string>, strategy?: AccessDecisionStrategy) =>
      isGranted(auth.roles, requiredRoles, strategy),
    [auth.roles],
  );

  const isEnabledFeaturesFn = useCallback(
    (features?: Array<FeatureFlagEnum>) => {
      if (isNil(features)) return true;
      return features?.every((feature) => {
        const featureFlagKey = featureFlagsLookup[feature];
        return application.features[featureFlagKey] === true;
      });
    },
    [application.features],
  );

  return {
    isGranted: isGrantedFn,
    isEnabledFeatures: isEnabledFeaturesFn,
  } as const;
};

export const useUserAccessControl = () => {
  const auth = useAuth();
  const user = auth.user as unknown as IUserAuth;
  const isGrantedFn = useCallback(
    (requiredRoles: Array<string>, strategy?: AccessDecisionStrategy) =>
      isGranted(
        user?.profile.realm_access.roles ?? [],
        requiredRoles,
        strategy,
      ),
    [user?.profile.realm_access.roles],
  );

  return {
    isGranted: isGrantedFn,
  } as const;
};
